import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/Dialog';
import type { ImportMsProjectApiResponse } from '@momentum/types';

interface ImportResultDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: ImportMsProjectApiResponse | null;
  onClose: () => void;
}

export const ImportResultDialog = ({
  open,
  onOpenChange,
  result,
  onClose,
}: ImportResultDialogProps) => {
  const { t } = useTranslation('goals');
  const isSuccess = result && result.errors === undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isSuccess ? (
              <>
                <CheckCircle2 className="h-5 w-5 text-primary" />
                {t('importSuccess')}
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5 text-destructive" />
                {t('importError.title')}
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isSuccess ? (
              <div className="mt-4 space-y-2">
                <p className="text-sm text-muted-foreground">
                  {t('importSuccessDescription')}
                </p>
                <div className="mt-4 rounded-lg bg-gray-50 p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-medium text-muted-foreground">
                        {t('goalsCreated')}
                      </p>
                      <p className="text-2xl font-bold text-foreground">
                        {result?.goalsCreated}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground">
                        {t('tasksCreated')}
                      </p>
                      <p className="text-2xl font-bold text-foreground">
                        {result?.tasksCreated}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 border-t border-gray-200 pt-4">
                    <p className="font-medium text-muted-foreground">
                      {t('totalItems')}
                    </p>
                    <p className="text-2xl font-bold text-foreground">
                      {result?.totalItems}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-4 space-y-2">
                <p className="text-sm text-destructive">
                  {result?.errors?.[0] || t('importError.generic')}
                </p>
              </div>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 flex justify-end">
          <Button onClick={onClose}>{t('close')}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
